import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { CircularProgress, CssBaseline } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import mixpanel from "mixpanel-browser";
import { type ComponentType, type FC, lazy, type ReactNode, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Navigate, Outlet, type RouteObject, useLocation, useNavigate, useParams, useRoutes } from "react-router-dom";
import { PageSkeleton } from "@/ui/Skeletons/PageSkeleton/PageSkeleton";
import type { SkeletonVariant } from "@/ui/Skeletons/PageSkeleton/skeletonVariants";
import { ForceUpdateScreen } from "./components/ForceUpdate/ForceUpdateScreen";
import { RequestUtilsProvider } from "./components/RequestData/Provider/RequestUtilsProvider";
import { MobileStaffing } from "./components/staffing/mobile/MobileStaffing";
import { MobileStaffingRouteSwitch } from "./components/staffing/mobileGaps/MobileStaffingRouteSwitch";
import { StaffingWrapper } from "./components/staffing/StaffingWrapper";
import { TermsAndConditionsDialog } from "./components/TermsAndConditionsDialog";
import { type UserInfo, useAuth } from "./contexts/AuthProvider";
import { AnalyticValuesProvider } from "./contexts/analytics/AnalyticValuesProvider";
import { ChoseDepartmentProvider } from "./contexts/ChosenDepartment";
import { useChosenDepartment } from "./contexts/ChosenDepartment/hooks";
import { CloseOpenShiftSuggestionProvider } from "./contexts/CloseOpenShiftSuggestionProvider";
import { DraftModeProvider } from "./contexts/DraftModeProvider";
import { FEATURE_FLAGS, FeatureFlagsProvider } from "./contexts/FeatureFlagsProvider";
import { HospitalDataProvider } from "./contexts/HospitalDataProvider";
import { I18nProvider } from "./contexts/i18nProvider";
import { ModalProvider } from "./contexts/ModalProvider";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { PopupManagerProvider } from "./contexts/PopupManagerContext";
import { RecommendationsProvider } from "./contexts/Recommendations/RecommendationsProvider";
import { RecommendationsDrawerProvider } from "./contexts/RecommendationsDrawerContext";
import { RequestsProvider } from "./contexts/RequestsContext";
import { ScheduleChangesProvider } from "./contexts/ScheduleChanges/ScheduleChangesProvider";
import { SelfSchedulingProvider } from "./contexts/SelfScheduling/SelfSchedulingProvider";
import { GlobalManualLocationOrderProvider } from "./contexts/Staffing/GlobalManualLocationOrderProvider";
import { UserSettingsProvider } from "./contexts/Staffing/UserSettingsProvider";
import { TooltipProvider } from "./contexts/TooltipContext";
import { UserGuideChatProvider } from "./contexts/UserGuideChatProvider";
import { WeekIndexProvider } from "./contexts/WeekIndexProvider";
import { WeekViewProvider } from "./contexts/WeekView";
import { RouteWeekProvider } from "./contexts/WeekView/RouteWeekProvider";
import { useIsTextEnlarged } from "./hooks/useIsTextEnlarged";
import { useIsMobile } from "./hooks/useMobile";
import { useOnReactClientInactive } from "./hooks/useOnReactClientInactive";
import { useFeatureFlag } from "./hooks/useOpenFeatureFlag";
import { usePushSettingsTracker } from "./hooks/usePushSettingsTracker";
import { RevealWageProvider } from "./hooks/useRevealWage";
import { useTextScale } from "./hooks/useTextScale";
import { useUniversalLink } from "./hooks/useUniversalLink";
import { loadCallIns, loadDaySchedule, loadHiringModule, loadPbjModelPage, loadScheduleTemplatePage } from "./pageLoaders";
import { BedsideHome } from "./pages/BedsideHome/BedsideHome";
import { LocationsPageSkeleton } from "./pages/Locations/LocationsPageSkeleton";
import { Layout } from "./pages/layout";
import { OtOverviewRoute } from "./pages/ManagementCenter/OtOverview/OtOverviewRoute";
import { usePbjModelRouteAccess } from "./pages/PbjModel/pbjModelAccess";
import { FramedRouteGuard } from "./pages/PublicCareers/FramedRouteGuard";
import PageNotFound from "./pages/pageNotFound/PageNotFound";
import SftpSetupPage from "./pages/SftpSetup/SftpSetupPage";
import { CreateAccount } from "./pages/signin/CreateAccount";
import { EmailVerification } from "./pages/signin/EmailVerification";
import { ForgotPassword } from "./pages/signin/ForgotPassword";
import { MultiFactorAuth } from "./pages/signin/MFA";
import { OneTimePassword } from "./pages/signin/OTP";
import { ResetPasswordOrWelcome } from "./pages/signin/ResetPasswordOrWelcome";
import { SignIn } from "./pages/signin/SignIn";
import { Welcome } from "./pages/signin/Welcome";
import { localStorageService } from "./services/LocalStorageService";
import { pushNotificationService } from "./services/pushNotification.service";
import { universalLinkService } from "./services/universalLink.service";
import { DepartmentLookSync } from "./theme/DepartmentLookSync";
import { HospitalDemoThemeProvider } from "./theme/HospitalDemoThemeProvider";
import { ThemePreferenceProvider } from "./theme/ThemePreferenceProvider";
import { SURFACE } from "./theme/tokens";
import { getAnalyticsIdentity, getAppVersion, getDeviceInfo, initAnalytics, setAnalyticsGlobalProperties, trackErrorEvent } from "./utils/analytics";
import { CALENDAR_CONNECTED_PATH } from "./utils/calendarConnectedPage";
import { CAREERS_APPLICATION_ROUTE, CAREERS_SEGMENT, careersPath } from "./utils/careersRoutes";
import { moduleUnlessReloading } from "./utils/chunkReload";
import { REDIRECT_TO_SESSION_STORE_KEY } from "./utils/consts";
import { HIRING_HUB_CANDIDATE_ROUTE, HIRING_HUB_SEGMENT } from "./utils/hiringHubRoutes";
import { AppPlatform, ProfileMode } from "./utils/models";
import { baseURL } from "./utils/networkWrapper";
import { PUBLIC_CAREERS_JOB_ROUTE, PUBLIC_CAREERS_ROUTE } from "./utils/publicCareers";
import { recurringShiftsPath } from "./utils/recurringShiftsRoutes";
import { rememberRedirectAfterLogin } from "./utils/redirectAfterLogin";
import { staffingWeeklyPath } from "./utils/staffingRoutes";
import { isBedSideNurse, isHeadNurse } from "./utils/utilFunctions";
import { compareSemver, EnforcementMode, fetchAppVersionConfig, type PlatformVersionConfig } from "./utils/versionCheck";

const ENV = import.meta.env.VITE_ENVIROMENT || "development";

/** `lazy` resolves only a module's default export; most pages export by name. */
const lazyNamed = <Key extends string, Props extends object>(load: () => Promise<Record<Key, ComponentType<Props>>>, key: Key) =>
	lazy(() =>
		load()
			.then(moduleUnlessReloading)
			.then((module) => ({ default: module[key] })),
	);
const withPageSuspense = (page: ReactNode, variant: SkeletonVariant): ReactNode => <Suspense fallback={<PageSkeleton variant={variant} />}>{page}</Suspense>;

const LazyOpsBackoffice = lazy(() => import("./pages/OpsBackoffice/OpsBackoffice"));
const LazyOpsBackofficeDrillDown = lazy(() => import("./pages/OpsBackoffice/OpsBackofficeDrillDown"));
const LazyVideoLibraryFixture = lazy(() => import("./pages/Dev/VideoLibraryFixture"));
const LazyVideoLibrary = lazy(() => import("./pages/VideoLibrary/VideoLibraryHome").then((module) => ({ default: module.VideoLibraryHome })));
const LazyVideoWatch = lazy(() => import("./pages/VideoLibrary/VideoWatchPage").then((module) => ({ default: module.VideoWatchPage })));
const LazySystemDataGridFixture = lazy(() => import("./pages/Dev/SystemDataGridFixture"));
const LazyConflictBlockedDialogFixture = lazy(() => import("./pages/Dev/ConflictBlockedDialogFixture"));
const LazyAirtableImportFixture = lazy(() => import("./pages/Dev/AirtableImportFixture"));
const LazyCalculateRecurringFixture = lazy(() => import("./pages/Dev/CalculateRecurringFixture"));
const LazyAgencyIntegrationToggleFixture = lazy(() => import("./pages/Dev/AgencyIntegrationToggleFixture"));
const LazyLocationsPage = lazy(() => import("./pages/Locations/LocationsPage"));
const LazyFillShiftReport = lazy(() => import("./pages/FillShiftReport/FillShiftReport"));
const LazyShiftReport = lazy(() => import("./pages/ShiftReport/ShiftReport"));
const LazyHiringHub = lazy(() => import("./pages/HiringHub/HiringHubPage"));
const LazyHiringHubProfile = lazy(() => import("./pages/HiringHub/profile/CandidateProfilePage"));
const LazyCareersPage = lazy(() => import("./pages/Careers/CareersPage"));
const LazyCareersCandidateProfile = lazy(() => import("./pages/Careers/profile/CandidateProfilePage"));
const LazyPublicCareersPage = lazy(() => import("./pages/PublicCareers/PublicCareersPage"));
const LazyCalendarConnectedPage = lazy(() => import("./pages/CalendarConnected/CalendarConnectedPage"));
// Route pages load on first visit, so each page's libraries (charts, animations, editors) stay out of the
// first load. Eager: the shell and route guards, the first screen each role lands on (sign-in, the bedside
// nurse's schedule, the head nurse's staffing grid), and the SFTP setup page, which renders outside the app's
// error boundary. CloudFront also compresses only files under 10,000,000 bytes, and with every page imported
// eagerly the entry chunk sat right at that limit.
const LazyCallIns = lazyNamed(loadCallIns, "CallIns");
const LazyCommandCenter = lazyNamed(() => import("./pages/CommandCenter/CommandCenter"), "CommandCenter");
const LazyDaySchedule = lazyNamed(loadDaySchedule, "DaySchedule");
const LazyDayScheduleMobile = lazyNamed(() => import("./pages/DaySchedule/Mobile/DayScheduleMobile"), "DayScheduleMobile");
const LazyNotificationsPage = lazyNamed(() => import("./pages/NotificationsPage/NotificationsPage"), "NotificationsPage");
const LazyNurseScheduleRoute = lazyNamed(() => import("./pages/NurseSchedule/NurseScheduleRoute"), "NurseScheduleRoute");
const LazyOpenShifts = lazyNamed(() => import("./pages/OpenShifts/OpenShifts"), "OpenShifts");
const LazyPatients = lazyNamed(() => import("./pages/Patients/Patients"), "Patients");
const LazyPreferences = lazyNamed(() => import("./pages/Preferences/Preferences"), "Preferences");
const LazySchedule = lazyNamed(() => import("./pages/Schedule/Schedule"), "Schedule");
const LazySelfSchedulingWrapper = lazyNamed(() => import("./pages/SelfScheduling/SelfSchedulingWrapper"), "SelfSchedulingWrapper");
const LazyChatBot = lazyNamed(() => import("./components/Chat/ChatBot"), "ChatBot");
const LazyChartContainer = lazyNamed(() => import("./components/chartContainer/ChartContainer"), "ChartContainer");
const LazyAdminUserGuides = lazyNamed(() => import("./components/docs/admin-user-guides/AdminUserGuides"), "AdminUserGuides");
const LazyStaffUserGuides = lazyNamed(() => import("./components/docs/staff-user-guides/StaffUserGuides"), "StaffUserGuides");
const LazyPlanningOverview = lazyNamed(() => import("./components/planning/PlanningOverview"), "PlanningOverview");
const LazyShiftData = lazyNamed(() => import("./components/ShiftData/ShiftData"), "ShiftData");
const LazyStaffCommunication = lazyNamed(() => import("./components/StaffCommunication/StaffCommunication"), "StaffCommunication");
const LazyMonthlyViewRoute = lazyNamed(() => import("./components/staffing/MonthlyView/jobView/MonthlyViewRoute"), "MonthlyViewRoute");
const LazyAddNurseToShift = lazyNamed(() => import("./components/staffing/mobile/AddNurseToShift/AddNurseToShift"), "AddNurseToShift");
const LazyBroadcastMessage = lazyNamed(() => import("./components/staffing/mobile/BroadcaseMessage/BroadcastMessage"), "BroadcastMessage");
const LazyPostShift = lazyNamed(() => import("./components/staffing/mobile/PostShift/PostShift"), "PostShift");
const LazyTeam = lazyNamed(() => import("./components/team/Team"), "Team");
const LazyRequestRouteWrapper = lazyNamed(() => import("./components/RequestData/RequestRouteWrapper"), "RequestRouteWrapper");
const LazyRequestsRouteSwitch = lazyNamed(() => import("./components/staffing/mobileGaps/RequestsRouteSwitch"), "RequestsRouteSwitch");
const LazyPbjCalculator = lazyNamed(() => import("./pages/PbjCalculator/PbjCalculator"), "PbjCalculator");
const LazyPbjModelPage = lazyNamed(loadPbjModelPage, "PbjModelPage");
const LazyProfileV2 = lazyNamed(() => import("./pages/Profile/v2/ProfileV2"), "ProfileV2");
const LazySettings = lazyNamed(() => import("./pages/Settings/Settings"), "Settings");
const LazyDepartmentConfiguration = lazyNamed(() => import("./pages/Settings/DepartmentConfiguration/DepartmentConfiguration"), "DepartmentConfiguration");
const LazyFacilityConfiguration = lazyNamed(() => import("./pages/Settings/FacilityConfiguration/FacilityConfiguration"), "FacilityConfiguration");
const LazyOnboardingToolsPage = lazy(() => import("./pages/OnboardingTools/OnboardingToolsPage"));
const LazyNotificationSettings = lazyNamed(() => import("./pages/Settings/Notifications/NotificationSetting"), "NotificationSettings");
const LazyAdminNotificationSettings = lazyNamed(() => import("./pages/Settings/Notifications/AdminNotificationSettings"), "AdminNotificationSettings");
const LazyNotificationTypeSettings = lazyNamed(() => import("./pages/Settings/Notifications/NotificationTypeSettings"), "NotificationTypeSettings");
const LazyScheduleTemplatePageWrapper = lazyNamed(loadScheduleTemplatePage, "ScheduleTemplatePageWrapper");
const LazyActivityLogPage = lazyNamed(() => import("./pages/ActivityLog/ActivityLogPage"), "ActivityLogPage");
const LazyEmployeeChangelog = lazyNamed(() => import("./pages/EmployeeChangelog/EmployeeChangelog"), "EmployeeChangelog");
const LazyHiringModuleWrapper = lazy(loadHiringModule);
const LazyDisciplinaryReport = lazy(() => import("./pages/DisciplinaryReport/DisciplinaryReport"));
const LazyLeakageReport = lazy(() => import("./pages/LeakageReport/LeakageReport"));
const LazyShiftPickupReport = lazy(() => import("./pages/ShiftPickupReport/ShiftPickupReport"));
const LazyManagementCenter = lazyNamed(() => import("./pages/ManagementCenter/ManagementCenter"), "ManagementCenter");
const LazyScheduleVsActualReport = lazyNamed(() => import("./pages/ManagementCenter/ScheduleVsActual/ScheduleVsActualReport"), "ScheduleVsActualReport");
const LazyCostDeepDiveReport = lazyNamed(() => import("./pages/ManagementCenter/CostDeepDive/CostDeepDiveReport"), "CostDeepDiveReport");
const LazyOtOverviewReport = lazyNamed(() => import("./pages/ManagementCenter/OtOverview/OtOverviewReport"), "OtOverviewReport");
const LazyHoursSummaryReport = lazyNamed(() => import("./pages/ManagementCenter/HoursSummary/HoursSummaryReport"), "HoursSummaryReport");
const LazyStaffSatisfactionReport = lazyNamed(() => import("./pages/ManagementCenter/StaffSatisfaction/StaffSatisfactionReport"), "StaffSatisfactionReport");
const LazyManagementCenterMulti = lazyNamed(() => import("./pages/ManagementCenterNetwork/ManagementCenterMulti"), "ManagementCenterMulti");
const LazyFacilityPage = lazyNamed(() => import("./pages/FacilityPage/FacilityPage"), "FacilityPage");
// The People roster pulls in PeopleGrid, ClickedCellEditor and StaffingChanges. Statically imported,
// all of that entered the initial bundle before `buildRoutes` had even evaluated the route's own
// rollout guard — a cost paid by every session for a view most of them cannot open. Named export, so
// the module is mapped to a default the way `lazy` requires.
const LazyStaffingPeopleViewRoute = lazy(() =>
	import("./components/staffing/PeopleView/StaffingPeopleViewRoute").then((module) => ({ default: module.StaffingPeopleViewRoute })),
);

import { useCareersAccess } from "./pages/Careers/hooks/useCareersAccess";
import { useHubTabAccessQuery } from "./pages/HiringHub/hooks/useHiringHub";
import { LEGACY_VIDEO_LIBRARY_MANAGE_PATH, VIDEO_LIBRARY_MANAGE_URL, VIDEO_LIBRARY_PATH } from "./pages/VideoLibrary/consts";

// ── Route access model ───────────────────────────────────────────────
type RouteCtx = { userInfo: UserInfo; isMobile: boolean; getDepartmentId: () => string | null };

/**
 * Mobile has no Monthly grid and no People roster, so both URLs fall back to the weekly grid.
 *
 * The path is built ABSOLUTELY on purpose. A relative `<Navigate to="staffing">` resolves against the
 * MATCHED route, so from `staffing/monthly/:date?` it produced `/:departmentId/staffing/monthly/staffing`
 * and the user landed on /404 rather than the grid. `useParams` is the honest source for the id here —
 * the route cannot match without it, so there is nothing to fall back to.
 */
const StaffingWeeklyRedirect: FC = () => {
	const { departmentId } = useParams();
	// Unreachable by construction — the route cannot match without the param — so home is the honest
	// destination rather than a thrown error: a crashed subtree is a heavier failure than a redirect that
	// lands one level wider than intended.
	return <Navigate to={departmentId === undefined ? "/" : staffingWeeklyPath(departmentId)} replace />;
};
/** "Schedule Template" was renamed to "Recurring Shifts" (APP-1008); bookmarks to the old URL still land on the page. */
const RecurringShiftsRedirect: FC = () => {
	const { departmentId } = useParams();
	const { search } = useLocation();
	// Unreachable by construction, as in `StaffingWeeklyRedirect` above.
	return <Navigate to={departmentId === undefined ? "/" : `${recurringShiftsPath(departmentId)}${search}`} replace />;
};
type AccessResult = true | { redirect: string };
type Access = (ctx: RouteCtx) => AccessResult;

// Presets — plain constants, each with its default deny target.
const PUBLIC: Access = () => true;
const ANY: Access = () => true;
// Reachable without login, but NOT open: the page validates a one-time link token
// (404s if missing) and the server rejects bad/expired tokens. Router passes through.
const TOKEN_GATED: Access = () => true;
const HEAD_NURSE: Access = ({ userInfo }) => (isHeadNurse(userInfo.role) ? true : { redirect: "/" });
const BEDSIDE: Access = ({ userInfo }) => (isBedSideNurse(userInfo.role) ? true : { redirect: "/" });
const SUPPORT: Access = ({ userInfo }) => (userInfo.support ? true : { redirect: "/404" });
const SUPPORT_OR_HEAD_NURSE: Access = ({ userInfo }) => (userInfo.support || isHeadNurse(userInfo.role) ? true : { redirect: "/" });

// ── Route node types + builder ───────────────────────────────────────
// A node is EITHER a guarded page, or a structural layout/prefix wrapper.
type PageNode = { path?: string; index?: boolean; access: Access; element: (ctx: RouteCtx) => ReactNode };
type LayoutNode = { path?: string; element?: ReactNode; children: RouteNode[] };
type RouteNode = PageNode | LayoutNode;

// Turn our nodes into React Router's route objects, applying the guard on each page.
function buildRoutes(nodes: RouteNode[], ctx: RouteCtx): RouteObject[] {
	return nodes.map((node) => {
		if ("access" in node) {
			const result = node.access(ctx); // allowed? or redirect?
			return {
				path: node.path,
				index: node.index,
				element: result === true ? node.element(ctx) : <Navigate to={result.redirect} replace />,
			} as RouteObject;
		}
		// layout/prefix: element defaults to <Outlet/> when omitted (a bare URL prefix)
		return { path: node.path, element: node.element, children: buildRoutes(node.children, ctx) } as RouteObject;
	});
}

const LoggedInRouteProviders: FC = () => {
	const { isLoggedIn, isCheckingAuth } = useAuth();
	const location = useLocation();
	const [shouldDelayLogin, setShouldDelayLogin] = useState(() => {
		// On mobile, always delay login redirect slightly on initial load
		// to give time for universal link detection
		return Capacitor.isNativePlatform();
	});

	useLayoutEffect(() => {
		// Check for pending universal link synchronously before paint
		if (!isLoggedIn && Capacitor.isNativePlatform()) {
			if (universalLinkService.hasPendingLaunchUrl()) {
				// Found a pending URL, delay longer to allow processing
				const timer = setTimeout(() => {
					setShouldDelayLogin(false);
				}, 2000);
				return () => clearTimeout(timer);
			}
			// No pending URL yet, but App.getLaunchUrl() might still be loading
			// Give it a brief moment
			const timer = setTimeout(() => {
				// Check again after a short delay
				if (universalLinkService.hasPendingLaunchUrl()) {
					// Found it, delay longer
					setTimeout(() => {
						setShouldDelayLogin(false);
					}, 1500);
				} else {
					setShouldDelayLogin(false);
				}
			}, 100);
			return () => clearTimeout(timer);
		}
		setShouldDelayLogin(false);
	}, [isLoggedIn]);

	if (!isLoggedIn) {
		// Don't redirect to login if we're still checking authentication or delaying for universal link
		if (isCheckingAuth || (shouldDelayLogin && Capacitor.isNativePlatform())) {
			return null; // Return nothing, preventing login page flash
		}
		rememberRedirectAfterLogin(location);
		return <Navigate to="/login" />;
	}

	return (
		<ChoseDepartmentProvider>
			<DepartmentLookSync />
			<FeatureFlagsProvider>
				<WeekIndexProvider>
					<TooltipProvider>
						<HospitalDataProvider>
							<SelfSchedulingProvider>
								<NotificationsProvider>
									<RequestsProvider>
										<ModalProvider>
											<RecommendationsDrawerProvider>
												<RevealWageProvider>
													<DraftModeProvider>
														<UserGuideChatProvider>
															<RequestUtilsProvider>
																<UserSettingsProvider>
																	<GlobalManualLocationOrderProvider>
																		<RecommendationsProvider>
																			<PopupManagerProvider>
																				<HospitalDemoThemeProvider>
																					<CloseOpenShiftSuggestionProvider>
																						<ScheduleChangesProvider>
																							<Outlet />
																						</ScheduleChangesProvider>
																					</CloseOpenShiftSuggestionProvider>
																				</HospitalDemoThemeProvider>
																			</PopupManagerProvider>
																		</RecommendationsProvider>
																	</GlobalManualLocationOrderProvider>
																</UserSettingsProvider>
															</RequestUtilsProvider>
														</UserGuideChatProvider>
													</DraftModeProvider>
												</RevealWageProvider>
											</RecommendationsDrawerProvider>
										</ModalProvider>
									</RequestsProvider>
								</NotificationsProvider>
							</SelfSchedulingProvider>
						</HospitalDataProvider>
					</TooltipProvider>
				</WeekIndexProvider>
			</FeatureFlagsProvider>
		</ChoseDepartmentProvider>
	);
};

const useGetDepartmentId = () => {
	//TODO: we should refactor the logic between ChosenDepartmentProvider and this hook to be more clear and not have duplicate logic around getting departmentId for redirects.
	// The main reason for this hook is to get departmentId in the RootRedirect component without having to use the ChosenDepartment context (which isn't set up yet at that level).
	// We can consider restructuring the app so that ChosenDepartmentProvider is available at a higher level, or we can centralize the logic for determining the "current" departmentId in a single place that both can use.

	// Helper hook to get departmentId for redirects
	const { userInfo } = useAuth();

	return () => {
		const storedDepartmentId = localStorageService.getChosenDepartmentID();
		if (storedDepartmentId && userInfo.departments?.some((dept) => dept.department_id === storedDepartmentId)) {
			return storedDepartmentId;
		}
		// Fall back to first department
		return userInfo.departments?.[0]?.department_id || null;
	};
};

const RootRedirect: FC = () => {
	const { userInfo } = useAuth();
	const isShiftInsightsEnabled = useFeatureFlag(FEATURE_FLAGS.ShiftInsightsTab);
	const careersAccess = useCareersAccess();
	const { chosenDepartment } = useChosenDepartment();
	const getDepartmentId = useGetDepartmentId();

	if (isBedSideNurse(userInfo.role)) return <Navigate to="/my-schedule" replace />;
	// Careers may be the landing page, and its flag is unknown until the department is chosen: hold here
	// rather than send a Careers user to Staffing on a cold load and never come back.
	if (careersAccess.loading) return <CircularProgress />;

	// Calculate redirect path for root route
	const getRootRedirectPath = (): string => {
		// With Careers open to them (`ats_careers` on and a hiring role here), Careers IS the landing page (bedside staff keep theirs, above). A deep link stored before login
		// still wins: the post-login effect in `App` navigates to it after this redirect has run.
		// Access is decided for the chosen department, so it is the one Careers opens on.
		if (careersAccess.allowed) {
			return careersPath(chosenDepartment.department_id);
		}

		const departmentId = getDepartmentId();
		if (isHeadNurse(userInfo.role) && isShiftInsightsEnabled) {
			return departmentId ? `/${departmentId}/shift-insights` : "/shift-insights";
		}

		return departmentId ? `/${departmentId}/staffing` : "/staffing:dateString?";
	};
	return <Navigate to={getRootRedirectPath()} replace />;
};

// Feature-flag gate lives INSIDE the element (rendered under FeatureFlagsProvider),
// not in `access` — the provider isn't available at the AppRoutes/buildRoutes level.
// Access = a hiring role (is_recruiter / is_interviewer / support), resolved by a backend call. While it's
// resolving we hold on a spinner rather than bouncing a legitimate role-user to "/". (The former `hiring_tab`
// feature flag was removed — access is role-based only.)
const useHiringTabAccess = (): { allowed: boolean; loading: boolean } => {
	const { data, isLoading } = useHubTabAccessQuery(true);
	if (isLoading) return { allowed: false, loading: true };
	return { allowed: data?.can_access ?? false, loading: false };
};

const HiringChatRoute: FC = () => {
	const { allowed, loading } = useHiringTabAccess();
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return (
		<Suspense fallback={<PageSkeleton variant="cards" />}>
			<LazyHiringHub />
		</Suspense>
	);
};

const HiringHubProfileRoute: FC = () => {
	const { allowed, loading } = useHiringTabAccess();
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return (
		<Suspense fallback={<PageSkeleton variant="detail" />}>
			<LazyHiringHubProfile />
		</Suspense>
	);
};

// Same shape as the Hiring routes: the gate lives INSIDE the element because the flag is read from
// `FeatureFlagsProvider`, which isn't mounted at the `buildRoutes` level.
const CareersRoute: FC<{ children: ReactNode; variant: SkeletonVariant }> = ({ children, variant }) => {
	const { allowed, loading } = useCareersAccess();
	// A cold load of /{department}/careers renders once before the department (and so its flag) is known.
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return <Suspense fallback={<PageSkeleton variant={variant} />}>{children}</Suspense>;
};

// One provider for every staffing page, so switching between them keeps the week's day items instead of rebuilding them.
const WeekViewRoutes: FC = () => (
	<RouteWeekProvider>
		<WeekViewProvider>
			<Outlet />
		</WeekViewProvider>
	</RouteWeekProvider>
);

// PBJ is filed for skilled nursing only: an AL or hospital department has no PBJ page, so a direct URL leaves
// the same way the Careers and Hiring routes do. In the element, like theirs, because the chosen department
// is only known below the route builder.
const PbjModelRoute: FC = () => {
	const { allowed, loading } = usePbjModelRouteAccess();
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return withPageSuspense(<LazyPbjModelPage />, "chart");
};

const routeRegistry: RouteNode[] = [
	{ path: "/login", access: PUBLIC, element: () => <SignIn /> },
	{ path: "/forgot-password", access: PUBLIC, element: () => <ForgotPassword /> },
	{ path: "/create-account", access: PUBLIC, element: () => <CreateAccount /> },
	{ path: "/reset-password", access: PUBLIC, element: () => <ResetPasswordOrWelcome /> },
	{ path: "/welcome", access: PUBLIC, element: () => <Welcome /> },
	{ path: "/otp", access: PUBLIC, element: () => <OneTimePassword /> },
	{ path: "/mfa", access: PUBLIC, element: () => <MultiFactorAuth /> },
	{ path: "/email-verification", access: PUBLIC, element: () => <EmailVerification /> },
	{ path: "/sftp-setup", access: TOKEN_GATED, element: () => <SftpSetupPage /> },
	// The public careers page: no login, outside LoggedInRouteProviders, and the only routes a framed window renders.
	{ path: PUBLIC_CAREERS_ROUTE, access: PUBLIC, element: () => withPageSuspense(<LazyPublicCareersPage />, "list") },
	{ path: PUBLIC_CAREERS_JOB_ROUTE, access: PUBLIC, element: () => withPageSuspense(<LazyPublicCareersPage />, "list") },
	// Where the interviewer calendar request email's link ends: no login, so it shows the same whoever opened the link.
	{ path: CALENDAR_CONNECTED_PATH, access: PUBLIC, element: () => withPageSuspense(<LazyCalendarConnectedPage />, "form") },
	{
		path: "/dev/system-datagrid",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="grid" />}>
				<LazySystemDataGridFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/video-library",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="cards" />}>
				<LazyVideoLibraryFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/conflict-blocked-dialog",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="form" />}>
				<LazyConflictBlockedDialogFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/airtable-config-import",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="form" />}>
				<LazyAirtableImportFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/calculate-recurring",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="form" />}>
				<LazyCalculateRecurringFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/agency-integration-toggle",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<PageSkeleton variant="form" />}>
				<LazyAgencyIntegrationToggleFixture />
			</Suspense>
		),
	},
	{
		element: <LoggedInRouteProviders />,
		children: [
			{ path: "/", access: ANY, element: () => <RootRedirect /> },
			{
				element: <Layout />,
				children: [
					{ path: "/my-schedule", access: BEDSIDE, element: () => <BedsideHome /> },
					{ path: "/annie", access: BEDSIDE, element: () => withPageSuspense(<LazyChatBot />, "list") },
					{ path: "/open-shifts", access: BEDSIDE, element: () => withPageSuspense(<LazyOpenShifts />, "list") },
					{ path: "/self-scheduling", access: BEDSIDE, element: () => withPageSuspense(<LazySelfSchedulingWrapper />, "timeline") },
					{ path: "/notifications", access: ANY, element: () => withPageSuspense(<LazyNotificationsPage />, "list") },
					{
						path: "/day-schedule/:date",
						access: ANY,
						element: (c) => withPageSuspense(c.isMobile || isBedSideNurse(c.userInfo.role) ? <LazyDayScheduleMobile /> : <LazyDaySchedule />, "timeline"),
					},
					{
						path: "/docs",
						children: [
							{ path: "admin-user-guides", access: HEAD_NURSE, element: () => withPageSuspense(<LazyAdminUserGuides />, "list") },
							{ path: "staff-user-guides", access: ANY, element: () => withPageSuspense(<LazyStaffUserGuides />, "list") },
						],
					},
					{ path: "/request/:id", access: ANY, element: (c) => withPageSuspense(<LazyRequestRouteWrapper getDepartmentId={c.getDepartmentId} />, "detail") },
					// Profile links from before Careers and Hiring carried a department (bookmarks, history). Matching them
					// here mounts `ChoseDepartmentProvider`, which rewrites them under the stored department; unmatched,
					// they fell through to `*` → /404. (`/careers` and `/hiring-chat` need no entry: `/:departmentId` matches them.)
					{ path: `/${CAREERS_APPLICATION_ROUTE}`, access: ANY, element: () => <CircularProgress /> },
					{ path: `/${HIRING_HUB_CANDIDATE_ROUTE}`, access: ANY, element: () => <CircularProgress /> },
					{
						path: "/ops-backoffice",
						access: SUPPORT,
						element: () => (
							<Suspense fallback={<PageSkeleton variant="grid" />}>
								<LazyOpsBackoffice />
							</Suspense>
						),
					},
					{
						path: VIDEO_LIBRARY_PATH,
						access: ANY,
						element: () => (
							<Suspense fallback={<PageSkeleton variant="cards" />}>
								<LazyVideoLibrary />
							</Suspense>
						),
					},
					{
						// Otherwise the old address would open the watch page for a video called "manage".
						path: LEGACY_VIDEO_LIBRARY_MANAGE_PATH,
						access: ANY,
						element: () => <Navigate to={VIDEO_LIBRARY_MANAGE_URL} replace />,
					},
					{
						path: `${VIDEO_LIBRARY_PATH}/:videoId`,
						access: ANY,
						element: () => (
							<Suspense fallback={<PageSkeleton variant="detail" />}>
								<LazyVideoWatch />
							</Suspense>
						),
					},
					{
						path: "/ops-backoffice/:departmentId",
						access: SUPPORT,
						element: () => (
							<Suspense fallback={<PageSkeleton variant="detail" />}>
								<LazyOpsBackofficeDrillDown />
							</Suspense>
						),
					},
					{
						path: "/:departmentId",
						children: [
							{ path: "preferences", access: ANY, element: () => withPageSuspense(<LazyPreferences />, "form") },
							{ path: "shift-insights", access: ANY, element: () => withPageSuspense(<LazyChartContainer />, "chart") },
							// The staffing pages share the shown week's day items; no other page computes them.
							{
								element: <WeekViewRoutes />,
								children: [
									// On a phone this forks between the gaps-first shell and the classic list — inside a
									// component, because the fork reads a feature flag and `FeatureFlagsProvider` renders
									// below this factory (see the note above `useHiringTabAccess`).
									{ path: "staffing/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffingRouteSwitch /> : <StaffingWrapper />) },
									// Classic keeps a URL of its own so "back to classic view" is linkable, survives a
									// reload, and survives a department switch (`resolveDepartmentSwitchDestination`). The
									// static segment outranks `:dateString?`, so "classic" is never read as a date.
									{ path: "staffing/classic/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffing /> : <StaffingWrapper />) },
									// Deliberately NOT forked: `staffing2` is a separate route that stays on classic.
									{ path: "staffing2/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffing /> : <StaffingWrapper />) },
									{
										path: "staffing/monthly/:date?",
										access: HEAD_NURSE,
										element: (c) => (c.isMobile ? <StaffingWeeklyRedirect /> : withPageSuspense(<LazyMonthlyViewRoute />, "timeline")),
									},
									// The People roster is a view of the Staffing area like Day/Week/Month, so it is a URL
									// like them — linkable, bookmarkable, and Back-able. The static segment outranks
									// `staffing/:dateString?`, so "people" is never read as a date.
									{
										path: "staffing/people",
										access: HEAD_NURSE,
										element: (c) =>
											c.isMobile ? (
												<StaffingWeeklyRedirect />
											) : (
												<Suspense fallback={<PageSkeleton variant="grid" />}>
													<LazyStaffingPeopleViewRoute />
												</Suspense>
											),
									},
									{
										path: "day-schedule/:date",
										access: ANY,
										element: (c) =>
											withPageSuspense(c.isMobile || isBedSideNurse(c.userInfo.role) ? <LazyDayScheduleMobile /> : <LazyDaySchedule />, "timeline"),
									},
								],
							},
							{
								path: "pbj-calculator",
								access: (c) => (isHeadNurse(c.userInfo.role) && !c.isMobile ? true : { redirect: "/" }),
								element: () => withPageSuspense(<LazyPbjCalculator />, "grid"),
							},
							{
								path: "pbj-model",
								access: (c) => (isHeadNurse(c.userInfo.role) && !c.isMobile ? true : { redirect: "/" }),
								element: () => <PbjModelRoute />,
							},
							{ path: "employee-staffing/:id/:draftMode", access: HEAD_NURSE, element: () => withPageSuspense(<LazyNurseScheduleRoute />, "timeline") },
							{ path: "command-center", access: HEAD_NURSE, element: () => withPageSuspense(<LazyCommandCenter />, "cards") },
							{ path: "team", access: HEAD_NURSE, element: () => withPageSuspense(<LazyTeam />, "grid") },
							{
								path: "add-nurse",
								access: HEAD_NURSE,
								element: () => withPageSuspense(<LazyProfileV2 initialProfileMode={ProfileMode.NEW} key="profile-create-only" />, "detail"),
							},
							{
								path: "nurse/:id",
								access: HEAD_NURSE,
								element: () => withPageSuspense(<LazyProfileV2 initialProfileMode={ProfileMode.VIEW} key="profile-view-or-edit" />, "detail"),
							},
							{ path: "settings", access: ANY, element: () => withPageSuspense(<LazySettings />, "form") },
							{ path: "settings/department-configuration", access: SUPPORT, element: () => withPageSuspense(<LazyDepartmentConfiguration />, "form") },
							{ path: "settings/facility-configuration", access: SUPPORT, element: () => withPageSuspense(<LazyFacilityConfiguration />, "form") },
							{ path: "onboarding/tools", access: SUPPORT, element: () => withPageSuspense(<LazyOnboardingToolsPage />, "form") },
							{
								path: "settings/notification-settings",
								access: ANY,
								element: (c) => withPageSuspense(isBedSideNurse(c.userInfo.role) ? <LazyNotificationSettings /> : <LazyAdminNotificationSettings />, "form"),
							},
							{ path: "planning", access: HEAD_NURSE, element: () => withPageSuspense(<LazyPlanningOverview />, "cards") },
							{ path: "request/:id", access: ANY, element: (c) => withPageSuspense(<LazyRequestRouteWrapper getDepartmentId={c.getDepartmentId} />, "detail") },
							{ path: "shift/:id", access: HEAD_NURSE, element: () => withPageSuspense(<LazyShiftData />, "detail") },
							{
								path: "post-shift/:dateString",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => withPageSuspense(<LazyPostShift />, "form"),
							},
							{ path: "recurring-shifts", access: HEAD_NURSE, element: () => withPageSuspense(<LazyScheduleTemplatePageWrapper />, "timeline") },
							{ path: "master-schedule", access: HEAD_NURSE, element: () => <RecurringShiftsRedirect /> },
							{
								path: "locations",
								access: HEAD_NURSE,
								element: () => (
									<Suspense fallback={<LocationsPageSkeleton />}>
										<LazyLocationsPage />
									</Suspense>
								),
							},
							{ path: "activity-log", access: SUPPORT_OR_HEAD_NURSE, element: () => withPageSuspense(<LazyActivityLogPage />, "grid") },
							{ path: "employee-changelog", access: HEAD_NURSE, element: () => withPageSuspense(<LazyEmployeeChangelog />, "grid") },
							{
								path: "requests",
								access: (c) => (c.isMobile && isHeadNurse(c.userInfo.role) ? true : { redirect: "/" }),
								element: () => withPageSuspense(<LazyRequestsRouteSwitch />, "list"),
							},
							{ path: "schedule", access: HEAD_NURSE, element: () => withPageSuspense(<LazySchedule />, "timeline") },
							{ path: "hiring", access: HEAD_NURSE, element: () => withPageSuspense(<LazyHiringModuleWrapper />, "cards") },
							{ path: HIRING_HUB_SEGMENT, access: ANY, element: () => <HiringChatRoute /> },
							{ path: HIRING_HUB_CANDIDATE_ROUTE, access: ANY, element: () => <HiringHubProfileRoute /> },
							{
								path: CAREERS_SEGMENT,
								access: ANY,
								element: () => (
									<CareersRoute variant="cards">
										<LazyCareersPage />
									</CareersRoute>
								),
							},
							{
								path: CAREERS_APPLICATION_ROUTE,
								access: ANY,
								element: () => (
									<CareersRoute variant="detail">
										<LazyCareersCandidateProfile />
									</CareersRoute>
								),
							},
							{ path: "call-ins", access: HEAD_NURSE, element: () => withPageSuspense(<LazyCallIns />, "list") },
							{ path: "disciplinary-report", access: HEAD_NURSE, element: () => withPageSuspense(<LazyDisciplinaryReport />, "grid") },
							{ path: "leakage-report", access: HEAD_NURSE, element: () => withPageSuspense(<LazyLeakageReport />, "chart") },
							{
								path: "fill-shift-report",
								access: HEAD_NURSE,
								element: () => (
									<Suspense fallback={<PageSkeleton variant="grid" />}>
										<LazyFillShiftReport />
									</Suspense>
								),
							},
							{
								path: "shift-report",
								access: SUPPORT_OR_HEAD_NURSE,
								element: () => (
									<Suspense fallback={<PageSkeleton variant="grid" />}>
										<LazyShiftReport />
									</Suspense>
								),
							},
							{ path: "shift-pickup-report", access: HEAD_NURSE, element: () => withPageSuspense(<LazyShiftPickupReport />, "grid") },
							{
								path: "settings/notification-settings/:notificationType",
								access: ANY,
								element: () => withPageSuspense(<LazyNotificationTypeSettings />, "form"),
							},
							{ path: "management-center", access: HEAD_NURSE, element: () => withPageSuspense(<LazyManagementCenter />, "cards") },
							{ path: "schedule-vs-actual", access: HEAD_NURSE, element: () => withPageSuspense(<LazyScheduleVsActualReport />, "chart") },
							{ path: "cost-deep-dive", access: HEAD_NURSE, element: () => withPageSuspense(<LazyCostDeepDiveReport />, "grid") },
							{
								path: "overtime-overview",
								access: HEAD_NURSE,
								element: () => <OtOverviewRoute>{withPageSuspense(<LazyOtOverviewReport />, "cards")}</OtOverviewRoute>,
							},
							{ path: "hours-summary", access: HEAD_NURSE, element: () => withPageSuspense(<LazyHoursSummaryReport />, "grid") },
							{ path: "staff-satisfaction", access: HEAD_NURSE, element: () => withPageSuspense(<LazyStaffSatisfactionReport />, "chart") },
							{ path: "management-center-multi", access: HEAD_NURSE, element: () => withPageSuspense(<LazyManagementCenterMulti />, "cards") },
							{ path: "patients/:dateString?", access: HEAD_NURSE, element: () => withPageSuspense(<LazyPatients />, "grid") },
							{ path: "facilities/:facilityId?/:facilityName?", access: HEAD_NURSE, element: () => withPageSuspense(<LazyFacilityPage />, "detail") },
							{
								path: "add-nurse-to-shift/:dateString",
								access: (c) => (isHeadNurse(c.userInfo.role) && c.isMobile ? true : { redirect: "/" }),
								element: () => withPageSuspense(<LazyAddNurseToShift />, "form"),
							},
							{
								path: "send-message",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => withPageSuspense(<LazyBroadcastMessage isMobile={true} />, "form"),
							},
							{
								path: "messages",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => withPageSuspense(<LazyStaffCommunication />, "list"),
							},
						],
					},
				],
			},
		],
	},
	{ path: "/404", access: PUBLIC, element: () => <PageNotFound /> },
	{ path: "*", access: PUBLIC, element: () => <Navigate to="/404" replace /> },
];

const AppRoutes: FC = () => {
	const { userInfo } = useAuth();
	const { isMobile } = useIsMobile();
	const getDepartmentId = useGetDepartmentId();

	const ctx: RouteCtx = { userInfo, isMobile, getDepartmentId };
	// The one gate for a framed window (a customer's website embedding the careers page): only the public careers
	// routes render there, because the server's framing headers are checked on the first document load only.
	const routes = useRoutes(buildRoutes(routeRegistry, ctx));
	return <FramedRouteGuard>{routes}</FramedRouteGuard>;
};

function App() {
	const { isLoggedIn, userInfo, showTermsDialog } = useAuth();
	const { isMobile } = useIsMobile();
	const location = useLocation();
	const [deviceInfo, setDeviceInfo] = useState<Awaited<ReturnType<typeof getDeviceInfo>> | null>(null);
	const [appVersion, setAppVersion] = useState<string>("unknown");
	const [versionEnforcement, setVersionEnforcement] = useState<{
		mode: EnforcementMode.SOFT | EnforcementMode.HARD;
		platformConfig: PlatformVersionConfig;
	} | null>(null);
	const [showSoftUpdate, setShowSoftUpdate] = useState(false);
	const analyticsInitialized = useRef(false);
	const textScale = useTextScale();
	const isTextEnlarged = useIsTextEnlarged();
	useUniversalLink();

	// Android's font size, so mobile events show which users get the enlarged-text layouts. Desktop layouts do not
	// adapt to it, so their events leave it out (undefined drops the property).
	const textSizeAnalyticValues = useMemo(
		() => ({ text_scale: isMobile ? textScale : undefined, is_text_enlarged: isMobile ? isTextEnlarged : undefined }),
		[isMobile, textScale, isTextEnlarged],
	);
	// The deprecated trackEvent/trackErrorEvent read module globals rather than the provider below.
	useEffect(() => {
		setAnalyticsGlobalProperties(textSizeAnalyticValues);
	}, [textSizeAnalyticValues]);

	// Fetch device info and app version when app loads (single source of truth for both useAnalytics and legacy trackEvent)
	useEffect(() => {
		const fetchInfo = async () => {
			if (Capacitor.isNativePlatform()) {
				const info = await getDeviceInfo();
				setDeviceInfo(info);
				const version = await getAppVersion();
				setAppVersion(version);
				// Update legacy globalProperties so deprecated trackEvent/trackErrorEvent also get device info + version
				setAnalyticsGlobalProperties({
					isApp: true,
					appVersion: version,
					deviceModel: info?.model ?? null,
					devicePlatform: info?.platform ?? null,
					deviceOsVersion: info?.osVersion ?? null,
					deviceManufacturer: info?.manufacturer ?? null,
				});
			}
		};
		fetchInfo();
	}, []);

	// Version enforcement check
	const checkAppVersion = useCallback(async (showSoft: boolean) => {
		if (!Capacitor.isNativePlatform()) return;
		const version = await getAppVersion();
		if (version === "unknown") return;
		const config = await fetchAppVersionConfig(baseURL);
		if (!config) return;
		const platform = Capacitor.getPlatform();
		const platformConfig = platform === AppPlatform.IOS ? config.ios : config.android;
		if (!platformConfig || platformConfig.enforcement_mode === EnforcementMode.OFF) {
			setVersionEnforcement(null);
			setShowSoftUpdate(false);
			return;
		}
		if (compareSemver(version, platformConfig.minimum_supported_version) < 0) {
			setVersionEnforcement({ mode: platformConfig.enforcement_mode, platformConfig });
			if (showSoft && platformConfig.enforcement_mode === EnforcementMode.SOFT) setShowSoftUpdate(true);
		} else {
			setVersionEnforcement(null);
			setShowSoftUpdate(false);
		}
	}, []);

	// On app launch: check version and show both soft and hard
	useEffect(() => {
		checkAppVersion(true);
	}, [checkAppVersion]);

	// On foreground resume: only re-check for hard mode (soft would be annoying)
	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;
		const listener = CapacitorApp.addListener("appStateChange", ({ isActive }) => {
			if (isActive) checkAppVersion(false);
		});
		return () => {
			listener.then((l) => l.remove());
		};
	}, [checkAppVersion]);

	useEffect(() => {
		const initializeStatusBar = async () => {
			// Only initialize for Android
			if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === AppPlatform.Android) {
				try {
					// Set status bar style
					await StatusBar.setStyle({ style: Style.Light });

					// Set background color
					await StatusBar.setBackgroundColor({ color: SURFACE });

					// Make sure status bar doesn't overlay the webview
					await StatusBar.setOverlaysWebView({ overlay: false });

					console.log("Android status bar initialized successfully");
				} catch (error) {
					console.error("Failed to initialize Android status bar:", error);
				}
			}
		};

		initializeStatusBar();
	}, []);

	useEffect(() => {
		if (analyticsInitialized.current) return;
		initAnalytics();
		analyticsInitialized.current = true;
	}, []);

	const handleSessionInactive = useCallback(() => {
		try {
			mixpanel.stop_session_recording();
		} catch (error) {
			trackErrorEvent("Failed to stop session recording", error);
		}
	}, []);

	const handleSessionReturnFromInactive = useCallback(() => {
		try {
			mixpanel.start_session_recording();
		} catch (error) {
			trackErrorEvent("Failed to start new session recording", error);
		}
	}, []);

	// Support and impersonation sessions are internal, not real customer usage, so their session
	// replays waste Mixpanel's limited replay quota. Keeping the recording hook disabled for them
	// means start_session_recording() never fires (impersonation counts as support: the acting human
	// is support even though userInfo describes the impersonated customer). See APP-697.
	const isSupportOrImpersonationSession = !!userInfo?.support || !!userInfo?.impersonated_by;

	useOnReactClientInactive({
		timeoutMs: 15 * 60 * 1000 /*15 minutes*/,
		hookDisabled: !(
			analyticsInitialized.current &&
			ENV !== "development" &&
			isLoggedIn &&
			!!userInfo?.role &&
			isHeadNurse(userInfo.role) &&
			!isSupportOrImpersonationSession
		),
		onInactive: handleSessionInactive,
		onInitAndActiveAgain: handleSessionReturnFromInactive,
	});

	useEffect(() => {
		const analyticsIdentity = getAnalyticsIdentity(userInfo);

		if (analyticsIdentity && analyticsInitialized.current) {
			mixpanel.identify(analyticsIdentity);
			ReactGA.set({ userId: analyticsIdentity });
		}
		// The UI language is synced from the saved preference by I18nProvider, its only owner.
	}, [userInfo]);

	const navigate = useNavigate();
	useEffect(() => {
		const timerId = setTimeout(() => {
			const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_STORE_KEY);

			if (isLoggedIn && typeof redirectTo === "string") {
				sessionStorage.removeItem(REDIRECT_TO_SESSION_STORE_KEY);
				navigate(redirectTo, { replace: true });
			}
		}, 1_000);

		return () => {
			clearTimeout(timerId);
		};
	}, [isLoggedIn, navigate]);

	// Initialize push notifications when user ID changes
	useEffect(() => {
		if (userInfo?.id) {
			pushNotificationService.initialize(userInfo.id);
		}
	}, [userInfo?.id]);

	usePushSettingsTracker();

	const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

	// Roles are per-department; `AnalyticValues` is a flat scalar map, so the chosen department's role
	// list is joined into one filterable string ("" when the user has no role in it).
	const chosenDepartmentId = localStorageService.getChosenDepartmentID();
	const adminRolesForAnalytics = (userInfo.departments?.find((dept) => dept.department_id === chosenDepartmentId)?.adminRoles ?? []).join(",");

	// Hard enforcement: block the entire app
	if (versionEnforcement?.mode === EnforcementMode.HARD) {
		return (
			<ThemePreferenceProvider>
				<CssBaseline />
				<I18nProvider>
					<ForceUpdateScreen mode={EnforcementMode.HARD} />
				</I18nProvider>
			</ThemePreferenceProvider>
		);
	}

	return (
		<ThemePreferenceProvider>
			<CssBaseline />
			{showSoftUpdate && versionEnforcement && <ForceUpdateScreen mode={EnforcementMode.SOFT} onDismiss={() => setShowSoftUpdate(false)} />}
			<I18nProvider>
				<AnalyticValuesProvider
					props={{
						userId: userInfo.id,
						username: getAnalyticsIdentity(userInfo),
						isLoggedIn,
						isMobile,
						...textSizeAnalyticValues,
						pathname: location.pathname,
						department_id: localStorageService.getChosenDepartmentID() || userInfo.departments?.map((d) => d.department_id).join(","),
						admin_roles: adminRolesForAnalytics,
						// Impersonation counts as support activity: the acting human is support even though
						// userInfo describes the impersonated customer. Both cases must be filterable in Mixpanel.
						is_support_user: !!userInfo.support || !!userInfo.impersonated_by,
						// The impersonator's identifier, tagged onto every event so impersonation sessions
						// are attributable to the acting human. Null (not undefined) for real customer
						// sessions so the property is present and queryable rather than silently absent.
						impersonated_by: userInfo.impersonated_by ?? null,
						isApp: Capacitor.isNativePlatform(),
						appVersion: appVersion ?? "unknown",
						deviceModel: deviceInfo?.model ?? null,
						devicePlatform: deviceInfo?.platform ?? null,
						deviceOsVersion: deviceInfo?.osVersion ?? null,
						deviceManufacturer: deviceInfo?.manufacturer ?? null,
					}}
				>
					<GoogleOAuthProvider clientId={googleClientId}>
						<AppRoutes />
						{showTermsDialog && <TermsAndConditionsDialog open={showTermsDialog} />}
					</GoogleOAuthProvider>
				</AnalyticValuesProvider>
			</I18nProvider>
		</ThemePreferenceProvider>
	);
}

export default App;
